import { useState, type FormEvent } from "react";
import { useApi } from "../useApi.js";
import { api, dateDe } from "../api.js";
import type { Role, UserRow } from "../types.js";
import { Page, roleLabel } from "../components/Shell.js";
import { TableSkeleton } from "../components/Skeleton.js";
import { toast } from "../toast.js";

export default function Users() {
  const { data, error, loading, reload } = useApi<UserRow[]>("/api/users");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("MITARBEITER");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function reset() {
    setName(""); setEmail(""); setPassword(""); setRole("MITARBEITER"); setFormError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.post("/api/users", { name, email, password, role });
      reset();
      setOpen(false);
      await reload();
      toast("Benutzer angelegt", "success");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Benutzer" sub={data ? `${data.length} Benutzer` : undefined}
          actions={<button className="btn pri" onClick={() => setOpen((o) => !o)}>
            {open ? "Abbrechen" : "Benutzer anlegen"}
          </button>}>

      {open && (
        <div className="card" style={{ marginBottom: 16, maxWidth: 440 }}>
          <div className="card-h"><h3>Neuer Benutzer</h3></div>
          <form className="card-b" onSubmit={submit}>
            {formError && <div className="err" role="alert">{formError}</div>}

            <div className="field">
              <label htmlFor="u-name">Name</label>
              <input id="u-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="u-email">E-Mail</label>
              <input id="u-email" type="email" required autoComplete="off"
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="u-pw">Passwort</label>
              <input id="u-pw" type="password" required minLength={8} autoComplete="new-password"
                     value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="u-role">Rolle</label>
              <select id="u-role" value={role} onChange={(e) => setRole(e.target.value as Role)}
                      style={{ width: "100%", padding: "9px 11px", background: "var(--surface-2)",
                               border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
                <option value="MITARBEITER">Mitarbeiter</option>
                <option value="ADMIN">Administrator</option>
              </select>
            </div>

            <button className="btn pri" type="submit" disabled={busy}
                    style={{ width: "100%", justifyContent: "center", marginTop: 6 }}>
              {busy ? "Wird angelegt…" : "Anlegen"}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-h">
          <h3>Alle Benutzer</h3>
          <span className="hint">Administrator: Benutzer &amp; Preise · Mitarbeiter: laufender Betrieb</span>
        </div>
        {loading && <TableSkeleton widths={[25, 30, 15]} />}
        {error && <div className="empty">{error}</div>}
        {data && (
          <div className="tbl-wrap"><table>
            <thead><tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Angelegt</th><th>Letzte Anmeldung</th></tr></thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id}>
                  <td><b>{u.name}</b></td>
                  <td className="mono" style={{ fontSize: 12.5 }}>{u.email}</td>
                  <td><span className={`pill ${u.role === "ADMIN" ? "info" : "mute"}`}>{roleLabel(u.role)}</span></td>
                  <td className="num">{dateDe(u.createdAt)}</td>
                  <td className="num">{u.lastLoginAt ? dateDe(u.lastLoginAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </Page>
  );
}
